import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Pencil } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CustomerModal, displayName } from "@/pages/Customers";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  inactive: "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400",
};
const TYPE_STYLES = {
  B2B: "bg-sky-50 text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold",
  B2C: "bg-slate-100 text-slate-600 dark:bg-ink-panel dark:text-slate-300",
};

const B2B_ROWS = [
  ["legal_name", "Legal / Company Name"], ["brand_name", "Brand / Trading Name"],
  ["primary_contact", "Primary Contact"], ["website", "Website"], ["country", "Country"],
];
const B2C_ROWS = [
  ["full_legal_name", "Full Legal Name"], ["preferred_name", "Preferred Name"],
  ["nationality", "Nationality"], ["country_of_residence", "Country of Residence"],
  ["date_of_birth", "Date of Birth"],
];
const SHARED_ROWS = [
  ["email", "Email"], ["phone", "Phone"], ["whatsapp", "WhatsApp"], ["address", "Address"],
];

export default function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("customers.edit");
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/customers/${customerId}`);
      setCustomer(r.data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>;
  if (!customer) return null;

  const rows = [...(customer.customer_type === "B2B" ? B2B_ROWS : B2C_ROWS), ...SHARED_ROWS, ["notes", "Notes"]];

  return (
    <div className="max-w-3xl space-y-6" data-testid="customer-detail-page">
      <button onClick={() => navigate("/customers")} data-testid="back-to-customers" className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-sky-600 dark:text-slate-400 dark:hover:text-ink-gold">
        <ArrowLeft className="h-4 w-4" /> Back to Customers
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono-tf text-xs font-semibold text-slate-500 dark:text-ink-gold" data-testid="customer-detail-id">{customer.customer_id}</span>
            <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${TYPE_STYLES[customer.customer_type]}`} data-testid="customer-detail-type">{customer.customer_type}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[customer.status] || STATUS_STYLES.inactive}`} data-testid="customer-status-badge">{customer.status}</span>
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text" data-testid="customer-detail-name">{displayName(customer)}</h1>
        </div>
        {canEdit && (
          <button onClick={() => setModal({ mode: "edit", customer })} data-testid="edit-customer-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
            <Pencil className="h-4 w-4" /> Edit
          </button>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="customer-detail-card">
        <dl className="divide-y divide-slate-100 dark:divide-ink-line">
          {rows.map(([key, label]) => (
            <div key={key} className="grid grid-cols-3 gap-4 px-5 py-3">
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</dt>
              <dd className="col-span-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200" data-testid={`customer-field-${key.replace(/_/g, "-")}`}>{customer[key] || "—"}</dd>
            </div>
          ))}
        </dl>
        <div className="border-t border-slate-100 px-5 py-3 text-[11px] text-slate-400 dark:border-ink-line">
          Created {String(customer.created_at).slice(0, 10)} · Updated {String(customer.updated_at).slice(0, 10)}
        </div>
      </div>

      {modal && <CustomerModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
